import { useEffect, useRef, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

import { ApiMarketStatus, ApiMarketType, useGetInfo, useRecentTrades } from '@ping/api';
import { Footer, Page } from '@ping/components';
import { GovernanceRequestNotice } from '@ping/components/GovernanceRequestNotice';
import { NoMarketsAvailableModal } from '@ping/components/NoMarketsAvailableModal';
import { WEBSOCKET_URL } from '@ping/configs';
import { t } from '@ping/helpers';
import { isListedMarket } from '@ping/helpers/access-scope.helper';
import { useDesktopDevice, useIsUserLoggedIn } from '@ping/hooks';
import { marketStore, selectedMarketInstrumentSelector, useMarketStore } from '@ping/stores/market.store';
import {
  isUserActivityStatusActiveSelector,
  isUserAdminSelector,
  isUserComplianceReviewerSelector,
  isUserTradeDataViewerSelector,
  isUserActivityStatusBannedSelector,
  useUserInformationStore,
} from '@ping/stores/userInformation.store';
import { Tabs, Toast } from '@ping/uikit';
import { ITrades, useMarketMoneyWS, useNotificationWS, useOrderBookWS, useTradeHistoryWS } from '@ping/websockets';

import { MarketHistory, MarketOrder, OrderBook, TradingChart, TradingRecords } from './components';
import style from './style.module.scss';

import type { NextPage } from 'next';
import { getExchangeType } from '@ping/utils/checkCompany';

/**
 * Returns the latest 50 recent trades based on the tradeId.
 *
 * @param {...ITrades[]} trades - An array of ITrades objects representing the trades.
 * @return {ITrades[]} An array of ITrades objects representing the latest 50 recent trades.
 */
const getLatestRecentTrades = (...trades: ITrades[]) => {
  return trades.sort((x, y) => y.tradeId - x.tradeId).slice(0, 50);
};

export const Trade: NextPage = () => {
  //
  // NOTE: heavy to load, prevent rendering. it's especially noticeable after
  //       successful login when we redirect authenticated users to the trade page.
  const isUserComplianceReviewer = useUserInformationStore(isUserComplianceReviewerSelector);
  const isUserTradeDataViewer = useUserInformationStore(isUserTradeDataViewerSelector);
  const isUserAdmin = useUserInformationStore(isUserAdminSelector);
  const [, setSelectedTab] = useState('chart');

  if (isUserComplianceReviewer) {
    return <Page />;
  }

  if (isUserTradeDataViewer && !isUserAdmin) {
    return <Page />;
  }

  const isUserLoggedIn = useIsUserLoggedIn();
  const isUserActivityStatusActive = useUserInformationStore(isUserActivityStatusActiveSelector);
  const isUserActivityStatusBanned = useUserInformationStore(isUserActivityStatusBannedSelector);

  useEffect(() => {
    if (isUserActivityStatusBanned) {
      Toast.error({ title: t('User is temporarily muted.') });
    }
  }, [isUserActivityStatusBanned]);

  const [, setQueryParamMarket] = useQueryParam('market', StringParam);
  useMarketMoneyWS(WEBSOCKET_URL);
  const selectedMarketInstrument = useMarketStore(selectedMarketInstrumentSelector);
  const [tradesList, setTradesList] = useState([]);
  const [showNoMarketsModal, setShowNoMarketsModal] = useState(false);
  const handledExchangeTypeRef = useRef<string | null>(null);

  // Get pairs data to check market types
  const pairsMarket = useGetInfo({
    query: {
      select: ({ pairs }) => new Map(Object.entries(pairs)),
    },
  });

  // Track exchange type changes from localStorage
  const [exchangeType, setExchangeType] = useState(() => getExchangeType());

  // Listen for localStorage changes (when user switches exchange type)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'company' || e.key === null) {
        setExchangeType(getExchangeType());
      }
    };

    // Also listen for custom storage events (for same-window changes)
    const handleCustomStorageChange = () => {
      setExchangeType(getExchangeType());
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('exchangeTypeChanged', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('exchangeTypeChanged', handleCustomStorageChange);
    };
  }, []);

  // Auto-select appropriate market based on exchange type
  useEffect(() => {
    if (!pairsMarket.data || pairsMarket.data.size === 0) return;

    const currentInstrument = selectedMarketInstrument?.toLowerCase();

    // Find current pair info
    const currentPairInfo = currentInstrument ? pairsMarket.data.get(currentInstrument) : null;

    // Helper: FO CanAccess-filters the catalog; Internal markets have hidden=true (legacy).
    const isMarketAvailable = (info: any) =>
      isListedMarket(info) && (!info.status || info.status === ApiMarketStatus.Open);

    // Map exchange types to market types
    const getTargetMarketType = (): ApiMarketType | null => {
      switch (exchangeType) {
        case 'commodity':
        case 'miax':
        case 'nasdaq':
        case 'ping':
          return ApiMarketType.Commodity;
        case 'stock':
          return ApiMarketType.Stock;
        case 'forex':
          return ApiMarketType.Forex;
        case 'crypto':
          return ApiMarketType.Cryptocurrency;
        default:
          return ApiMarketType.Cryptocurrency; // Default to crypto if unknown
      }
    };

    const targetMarketType = getTargetMarketType();

    const isCurrentMarketValid =
      currentPairInfo && currentPairInfo.marketType === targetMarketType && isMarketAvailable(currentPairInfo);

    if (!isCurrentMarketValid) {
      let matchingPair: [string, any] | null = null;

      // Try to find a valid market of the target type
      for (const [key, info] of pairsMarket.data.entries()) {
        if (info.marketType === targetMarketType && isMarketAvailable(info)) {
          matchingPair = [key, info];
          break;
        }
      }

      if (!matchingPair) {
        // No matching market for target type - try to find ANY available market
        const anyAvailableMarket = Array.from(pairsMarket.data.entries()).find(([, info]) => isMarketAvailable(info));

        if (anyAvailableMarket) {
          // Show modal only if we haven't already for this exchange type
          if (handledExchangeTypeRef.current !== exchangeType) {
            setShowNoMarketsModal(true);
            handledExchangeTypeRef.current = exchangeType;
          }
          matchingPair = anyAvailableMarket;
        } else if (pairsMarket.data.size > 0) {
          // No non-hidden markets available at all - show modal and remove URL param
          if (handledExchangeTypeRef.current !== exchangeType) {
            setShowNoMarketsModal(true);
            handledExchangeTypeRef.current = exchangeType;
            setQueryParamMarket(undefined);
          }
          return;
        }
      } else {
        setShowNoMarketsModal(false);
        handledExchangeTypeRef.current = null;
      }

      if (matchingPair) {
        const [, info] = matchingPair;
        const base = info.baseAsset?.toUpperCase();
        const quote = info.quoteAsset?.toUpperCase() || 'USDC';
        const newMarket = `${base}/${quote}`;
        // Only update if we're selecting a different market
        if (newMarket.toLowerCase().replace('/', '_') !== currentInstrument) {
          marketStore.setSelectedMarket(newMarket);
        }
      }
    } else {
      setShowNoMarketsModal(false);
      handledExchangeTypeRef.current = null;
    }
  }, [pairsMarket.data, exchangeType]);

  const books = useOrderBookWS();
  const trades = useTradeHistoryWS();
  const isDesktop = useDesktopDevice();
  //
  // Since we need the status of the notification WebSocket in the SocketConnectivityIndicator component, I put it here.
  useNotificationWS();
  const { data } = useRecentTrades({
    Instrument: selectedMarketInstrument,
  });

  useEffect(() => {
    if (data) {
      setTradesList(data);
    }
  }, [data]);

  useEffect(() => {
    if (!trades) {
      return;
    }

    setTradesList(previous => getLatestRecentTrades(...previous, ...trades));
  }, [trades]);

  useEffect(() => {
    setQueryParamMarket(selectedMarketInstrument);
  }, [selectedMarketInstrument]);

  /* force re-render for OrderBook to make ScrollTo works since it doesn't work when the component is
   hidden with css style */
  const handleSelectedTab = (key: string) => {
    setSelectedTab(key);
  };

  return (
    <>
      {isUserLoggedIn && !isUserActivityStatusActive && <Page />}
      <NoMarketsAvailableModal isOpen={showNoMarketsModal} onClose={() => setShowNoMarketsModal(false)} />
      <Page>
        <GovernanceRequestNotice />
        <div className={style['main-container']}>
          <MarketOrder />

          {!isDesktop && (
            <Tabs.Root
              className={style['trade-widgets-tabs']}
              variant='secondary'
              defaultSelectedKey='chart'
              onSelectionChange={handleSelectedTab}
              isEven
              isMountable
              disabledKeys={!isUserLoggedIn ? ['orders'] : []}
            >
              <Tabs.Item key='charts' title={t('Charts')}>
                <TradingChart />
              </Tabs.Item>
              <Tabs.Item key='book' title={t('Book')}>
                <OrderBook
                  selectedMarketInstrument={selectedMarketInstrument}
                  orderBookData={books}
                  tradesList={tradesList}
                />
              </Tabs.Item>
              <Tabs.Item key='trades' title={t('Trades')}>
                <MarketHistory selectedMarket={selectedMarketInstrument} tradesList={tradesList} />
              </Tabs.Item>
              <Tabs.Item key='orders' title={t('Orders')}>
                <TradingRecords />
              </Tabs.Item>
            </Tabs.Root>
          )}

          {isDesktop && (
            <>
              <TradingChart />
              <OrderBook
                selectedMarketInstrument={selectedMarketInstrument}
                orderBookData={books}
                tradesList={tradesList}
              />
              <MarketHistory selectedMarket={selectedMarketInstrument} tradesList={tradesList} />
              <TradingRecords />
            </>
          )}
        </div>
        <Footer />
      </Page>
    </>
  );
};

export default Trade;
